import loadSearchAmplitudes from "./search/search_amplitudes";

export const onLoad = () => {
  loadSearchAmplitudes();
};

export default String.raw`
<div id="EssayContents" data-essayversion="13" data-cardcount="36"><div id="EssayContentsInner">

<div class="BodyColumn">

  <p>
    Imagine you&rsquo;re the star of an action movie about a kidnapping. As
    part of the story, you come into possession of a secret message,
    which says where the victim is hidden. Unfortunately, the message
    is encrypted using a 12-digit secret key, i.e., a string of digits
    such as $8409\ldots\ $. But you don&rsquo;t know the secret key. The
    only way to unlock the message and find the victim is by searching
    through the $N = 10^{12}$ (one trillion) possible keys. While you
    may get lucky and find the right key early on, on average you&rsquo;ll
    need to try $N/2$ different keys, and in the worst case you&rsquo;ll
    need to try all $N$.
  </p>

  <p>
    I&rsquo;ve painted a fanciful picture, but similar search-based
    algorithms are used frequently in computing. They&rsquo;re often
    the first approach we try when solving a problem. For example,
    suppose you&rsquo;re trying to attack the
    famous <a href="https://en.wikipedia.org/wiki/Travelling_salesman_problem">traveling
    salesperson problem</a> (TSP), that is, trying to find the
    shortest route that visits every city in a list of cities, while
    returning to the origin city. A simple approach is to search
    through all the possible routes, while keeping track of the
    minimal route found. Of course, it&rsquo;s possible to develop
    more sophisticated algorithms for TSP, algorithms that make it
    unnecessary to search through every route. But that doesn&rsquo;t
    take away from the core point: for many problems in computing a
    search-based approach is a good first-cut way to attack the
    problem. Indeed, search is sometimes a good final-cut approach, or
    even provably optimal. Overall, search is an exceptionally useful
    general-purpose algorithm.
  </p>

  <p>
    As mentioned above, on a conventional classical computer, if we
    have a search space of $N$ items, we need to examine the search
    space on the order of $N$ times to find the item we&rsquo;re looking
    for.  Remarkably, quantum computers can do far better. It turns
    out that you can use a quantum computer to solve the search
    problem after examining the search space roughly $\sqrt{N}$ times!
    A little more precisely, it needs to examine the search space
    about $\pi\sqrt{N}/4$ times. That square root factor makes a big
    difference. If $N$ was a trillion, as in our opening scenario,
    then a classical computer will need to examine the search space a
    trillion times, while the quantum computer will need to examine it
    fewer than 800 thousand times. That&rsquo;s an improvement of more than
    a factor of a million.
  </p>

  <p>
    When I first heard about the quantum search algorithm I thought it
    sounded impossible.  I just couldn&rsquo;t imagine any way it
    could be true. But it is true.  In this essay I explain in detail
    how the quantum search algorithm works. I&rsquo;ll also explain
    some limitations of the quantum search algorithm, and discuss what
    we can learn about quantum computing in general from the quantum
    search algorithm.
  </p>

  <p>
    To read this essay you need to be familiar with the quantum
    circuit model of computation. If you&rsquo;re not, you can learn
    the elements from the earlier
    essay <a href="https://quantum.country/qcvc">Quantum Computing for
    the Very Curious</a>.
  </p>

  <p>
    It may be tempting to think &ldquo;Oh, I'm not that interested in
    the problem of search, why should I bother learning about
    it?&rdquo; But the point of this essay is deeper than search. It's
    to begin answering the question: how can we use quantum computers
    to do things which are genuinely different and better than a
    conventional classical computer?  The particular problem (search)
    is almost incidental. And so the essay is about learning to think
    in the quantum realm, finding non-classical heuristics that let us
    beat classical computers. This turns out to be immensely
    challenging, but also immensely fun.
  </p>

  <p>
    Because of these aspirations, I won&rsquo;t just explain how the
    search algorithm works.  We'll dig down and try to understand why
    it works, and how you might have discovered the algorithm in the
    first place.  That takes more time than just laying out the
    quantum circuit, but is also more rewarding.  Along the way
    we&rsquo;ll learn many other techniques widely used in quantum
    algorithm design, ideas such as clean computation, the phase
    trick, quantum parallelism, and others.  All this is great
    experience in learning how to think about quantum algorithm design
    in general.
  </p>
  
  <p>
    This essay is an example of what Andy Matuschak and I have dubbed
    a <em>mnemonic medium</em> &ndash; it&rsquo;s like a regular
    essay, but incorporates new user interface elements intended to
    make it almost effortless for you to remember the content of the
    essay. The motivator is that most people (myself included) quickly
    forget much of what we read in books and articles. But cognitive
    scientists studying human memory have understood how to guarantee
    you will remember something permanently.  This mnemonic medium
    builds those ideas into the essay, making it easy to remember the
    material for the long term.
  </p>

  <p>
    The core idea of the mnemonic medium is this: throughout the essay
    we occasionally pause to ask you a few simple questions, testing
    you on the material just explained.  In the weeks ahead
    we&rsquo;ll re-test you in followup review sessions.  By carefully
    expanding the testing schedule, we can ensure you consolidate the
    answers into your long-term memory, while minimizing the study
    time required.  The review sessions take no more than a few minutes
    per session, and we&rsquo;ll notify you when you need to review. The benefit
    is that instead of remembering how the quantum search algorithm
    works for a few hours or days, you&rsquo;ll remember for years; it&rsquo;ll
    become a much more deeply internalized part of your thinking.
  </p>

  <p class="newUser">
    Of course, you can just read this as a conventional essay.  But I
    hope you&rsquo;ll at least try out the mnemonic medium.  To do so please
    sign up below. This will enable us to track the best review
    schedule for each question, and to remind you to sign in for
    occasional short review sessions.  And if you&rsquo;d like to learn more
    about how the mnemonic medium works, please
    see <a href="/qcvc#a-medium-which-makes-memory-a-choice">A medium
    which makes memory a
    choice</a>, <a href="/qcvc#how-to-approach-this-essay">How to
    approach this essay?</a>,
    and <a href="/qcvc#how-to-use-or-not-use">How to use (or not use!)
    the questions</a>.
  </p>

  <div class="Login"></div>

  <p>
    As an example, let&rsquo;s take a look at a couple of simple
    questions reviewing what you&rsquo;ve just learned. Please indulge
    me by answering the questions just below.  It&rsquo;ll only take a
    few seconds &ndash; for both questions, think about what you
    believe the answer to be, click to reveal the actual answer, and
    then mark whether you remembered or not. If you can recall,
    that&rsquo;s great. If not, that&rsquo;s also fine, just mentally
    note the correct answer, and continue.  Since you probably weren't
    expecting to be tested like this, it seems only fair to give you a
    hint for the second question: the somewhat hard-to-remember
    prefactor in the answer is $\pi/4$.  Later in the essay I won't
    always provide such reminders, so you'll need to be paying
    attention!
  </p>

    
    <div class="review_set">
      <div class="card" data-cardid="4yFr6FVYlGV296VnyKaQ">
	<div class="question">To search a space of $N$ items, how many
	  times do we need to examine the search space on a classical
	  computer?
	</div>
	<div class="answer">$N$
	</div>
      </div>

      <div class="card" data-cardid="zu6X66RQgsRM5DaNafci">
	<div class="question">To search a space of $N$ items, roughly
	  how many times do we need to examine the search space on a
	  quantum computer?
	</div>
	<div class="answer">Roughly $\frac{\pi \sqrt{N}}{4}$
	</div>
      </div>

    </div>
    
  <h2 id="building-blocks-quantum-search">The building blocks of the
  quantum search algorithm</h2>

  <p>
    In the introduction I gave an informal description of what the
    quantum search algorithm achieves. To make the search algorithm
    more concrete, let&rsquo;s think about the special case of using
    search to attack the traveling salesperson problem (TSP). Of
    course, there are better approaches to TSP than search, but the
    purpose of this section is to show the overall building blocks
    that go into the search algorithm. For that purpose, TSP is a
    useful concrete example. In the next section we&rsquo;ll
    understand the details of how the buildings blocks work.
  </p>

  <p>
    It&rsquo;ll help to consider a variation on TSP, namely, searching for a
    route shorter than some specified threshold distance, $T$.  In
    other words, we&rsquo;ll be using search to solve problems like:
  </p>

  <blockquote>
    Here&rsquo;s a list of cities &ndash; Hobbiton, Minas Tirith, Edoras,
    Bree, Dale, &hellip; &ndash; and the distances between them (which
    I won&rsquo;t attempt to specify, but you can imagine!)  Is there a
    route through all the cities that is less than 2,000 kilometers
    [or the equivalent in miles] in length?
  </blockquote>

  <p>
    This isn&rsquo;t quite the same as find-the-minimal-route, but this
    variation turns out to be a little easier to connect to the
    quantum search algorithm.  Variation noted, here&rsquo;s what a quantum
    search algorithm might look like:
  </p>

  <p>
    <center>
      <img src="/assets2/search_route.png" class="RetinaImage">
    </center>
  </p>

  <p>
    The search register contains candidate solutions $|x\rangle =
    |x_1, x_2, \ldots, x_n\rangle$ to the search problem. In this
    case, our search register will contain potential routes through
    the cities, written out as bit strings $x = x_1, x_2, \ldots$. I
    won&rsquo;t get into the bit string representation explicitly &ndash;
    there are many ways to make such a representation, and the details
    don&rsquo;t much matter. The key point is that you should think of the
    search register as being in some superposition $\sum_x \alpha_x
    |x\rangle$ of different possible routes through the cities, and
    $x$ as being some bit string representation of a route.
  </p>

  <p>
    For definiteness, I&rsquo;ll also assume the search register starts in
    the all $|0\rangle$ state. That&rsquo;s just a convention: we need to
    start somewhere.
  </p>

  <p>
    Step 1 of the quantum search algorithm will just be some fixed
    quantum circuit, made up of standard quantum gates &ndash; things
    like the Hadamard and CNOT gates, as discussed in the
    <a href="https://quantum.country/qcvc">previous essay</a>. Of
      course, eventually we need to figure out what those gates should
      be.  We&rsquo;ll do that in later sections.  But for now
      we&rsquo;re just sticking at a broad conceptual level, trying to
      figure out what a quantum search algorithm might look like.
  </p>

  <p>
    The next step is to check if the search register state $|x\rangle$
    corresponds to what we&rsquo;ll call a <em>short route</em>
    through the cities, i.e., a route of less distance than the
    threshold $T$. To do this, we introduce a check qubit to store the
    results of this step, initialized in the state $|0\rangle$. So we
    start in the state $|x\rangle|0\rangle$, and change to
    $|x\rangle|1\rangle$ if $x$ represents a short route through the
    cities, and otherwise are left as $|x\rangle|0\rangle$, when $x$
    doesn&rsquo;t represent a short route. We can write this compactly
    as $|x\rangle|0\rangle \rightarrow |x\rangle|s(x)\rangle$, where
    the <em>search function</em> $s(x)$ is equal to $1$ if $x$ is a
    solution to the problem (i.e., a route of length less than $T$),
    and $0$ if $x$ is not a solution.
  </p>

  <p>
    Of course, in general the search register is in a superposition
    $\sum_x \alpha_x |x\rangle$. We&rsquo;ll assume (and justify later) that
    this checking-if-short-route step acts linearly, taking $\sum_x
    \alpha_x |x\rangle |0\rangle$ to $\sum_x \alpha_x |x\rangle
    |s(x)\rangle$.
  </p>

  <p>
    How is this checking-if-short-route step implemented? Of course,
    in principle it&rsquo;s easy to construct a conventional classical
    circuit which does the trick &ndash; the circuit would just check
    that the bit string $x = x_1 x_2 \ldots$ is a valid route through
    all the cities, and if so would add up the corresponding
    distances, and compare it to the threshold $T$.  We can just take
    that classical circuit &ndash; whatever it is &ndash; and
    translate it into the equivalent quantum circuit. I explained how
    to do such translations using Toffoli and NOT gates in
    the <a href="/qcvc#what-are-quantum-computers-good-for">earlier
    essay</a>, and I won&rsquo;t re-explain it here.  Of course, we
    still need to figure out the exact details of the classical
    circuit, but: (a) that&rsquo;s part of classical computing, not
    quantum computing; and (b) in any case is a detail unrelated to
    making search work. With one slight caveat (to be discussed
    shortly), we&rsquo;ll take for granted we have a quantum circuit
    which can do the job.
  </p>

  <p>
    After that is Step 2 in the quantum search algorithm. Again, we
    need to figure out exactly what quantum gates to use here, and
    we&rsquo;ll do that in the next section.
  </p>

  <p>
    Next, we check again if the search register state $|x\rangle$ is a
    short route. It works just as before, with a check qubit and so
    on.
  </p>

  <p>
    We continue in this way, alternating steps in our search algorithm
    with checking whether or not the search register state is a
    solution to our search problem, i.e., a short route through the
    cities.  At the end of the algorithm we measure the search
    register.  If we&rsquo;ve designed the search algorithm well, then the
    result of the measurement will be a solution $s$ to the search
    problem, in this case a route through the cities of distance less
    than $T$.
  </p>

  <p>
    A challenge is that sometimes such a solution may not exist. In
    our example, that&rsquo;ll happen when there is no route through the
    cities of distance less than $T$. In that case, whatever
    measurement result we get at the end of the search algorithm, it
    won&rsquo;t be a solution to the search problem. That&rsquo;s okay, though,
    since it&rsquo;s easy to just check and make sure we&rsquo;ve got a legitimate
    solution.
  </p>

  <p>
    I&rsquo;ve been talking about the problem of searching for short routes
    in TSP. But there&rsquo;s little here that has to do with the details of
    TSP. We can imagine a general quantum search algorithm which works
    along the same lines:
  </p>

  <p>
    <center>
      <img src="/assets2/search_broad.png" class="RetinaImage">
    </center>
  </p>

  <p>
    Everything is the same, except that we&rsquo;ve replaced the
    check-if-short-route step by $C_s$. We can think of this as a
    subroutine or black box which checks whether or not the search
    register is a solution $s$ to the search problem. In particular,
    we&rsquo;ll assume that $C_s$ takes $\sum_x \alpha_x
    |x\rangle|0\rangle$ to $\sum_x \alpha_x |x\rangle|s(x)\rangle$,
    where (to recap) the search function $s(x) = 0$ when $x$ is not a
    solution to the search problem, and $s(x) = 1$ when $x$ is a
    solution to the search problem.  More informally, we can think of
    $C_s$ as examining the search space to see if the search register
    contains a solution to the search problem. The hope motivating the
    quantum search algorithm is that we can reduce the number of times
    we need to do such examinations. In particular, we&rsquo;ll try to
    minimize the number of times the search black box $C_s$ needs to
    be applied.
  </p>

  <P>
    As another example, suppose the search problem is the one I opened
    the essay with &ndash; searching for a key to decode a
    kidnapper&rsquo;s note. In that case, you&rsquo;d design $C_s$ so
    it does two things: (1) decodes the kidnapper&rsquo;s note,
    assuming the search register contains a possible key; and (2)
    examines the decoded text from step 1 to see whether or not
    it&rsquo;s plausibly a message in English. If it is a plausibly an
    English message then almost certainly it&rsquo;s the correct text,
    since for most ciphers decodings for anything other than the
    correct key will look like gibberish. All of this is easily done
    using classical circuits, and those classical circuits can then be
    converted into a suitable quantum circuit for $C_s$.
  </P>

  <p>
    As still another example, consider the protein folding problem
    &ndash; the problem of figuring out what shapes proteins take on
    in nature. A way of phrasing this in our framework is as a search
    for a way of spatially arranging the protein&rsquo;s amino acids
    so the protein&rsquo;s energy is below some threshold energy, $E$?
    If you can answer this question reliably, then by gradually
    lowering the threshold $E$ you can find the lowest-energy states
    for the protein. These lowest-energy states correspond to the
    shapes we find in nature. Again, it&rsquo;s easy to figure out a
    circuit $C_s$ which checks whether or not some potential spatial
    arrangement of the amino acids has energy less than $E$.
  </p>
    
  <p>
    For the purpose of designing the quantum search algorithm
    we&rsquo;re not going to worry about how the search black box
    $C_s$ works. We&rsquo;ll just assume you&rsquo;ve got access to a
    suitable $C_s$.  Indeed, much of the utility of the quantum search
    algorithm comes from the fact that it works with any $C_s$.  Of
    course, to actually implement the quantum search algorithm in
    practice we&rsquo;d need to have an actual implementation of a
    suitable $C_s$. But to design a useful quantum search algorithm,
    we can treat $C_s$ as a black box.
  </p>
  
  <p>
    So our main job through the remainder of this essay is to figure
    out how to design the quantum circuits for step 1, step 2, and so
    on, in order to minimize the total number of times we need to
    apply the search black box. We&rsquo;ll design those quantum circuits in
    the next section.
  </p>

  <p>
    Incidentally, people new to the quantum search algorithms
    sometimes get a little hung up because of the slightly
    mysterious-sounding term &ldquo;black box&rdquo;. They worry that
    it implies there&rsquo;s some sort of sleight-of-hand or magic going on,
    that quantum search must require some sort of genie wandering
    around giving out black boxes. Of course, it&rsquo;s not magical at
    all. To repeat what I said above: if you were actually running the
    search algorithm, you&rsquo;d need an implementation of the black box
    for your particular problem. But the point is to design a search
    algorithm which works no matter the internal details of the search
    black box &ndash; it abstracts those away.
  </p>

  <p>
    Another common misconception is that to implement the search black
    box $C_s$ we would need to know the value of $s$ in
    advance. That&rsquo;s not necessary because there&rsquo;s a big difference
    between a circuit which can recognize a solution and which knows
    the solution. All the search black box needs is to be able to
    recognize a solution.  For instance, it&rsquo;s obviously possible to
    design a circuit which can recognize a short tour through a list
    of cities, without explicitly knowing a short tour in advance.
    Similarly for recognizing low-energy protein shapes, recognizing a
    decoded kidnapper&rsquo;s note, and so on.
  </p>
    
  <p>
    Having spent so much time saying that we&rsquo;re not going to worry
    about the details of $C_s$ I&rsquo;ll now turn around and say that it
    simplifies things a little if we make one extra assumption about
    the search black box: we&rsquo;ll suppose there is <em>exactly one</em>
    solution $s$ to the search problem. This assumption is ultimately
    not essential &ndash; the search algorithm can be extended to the
    case of multiple (or zero) solutions. But for now it simplifies
    life to assume there&rsquo;s exactly one single solution, which we&rsquo;ll
    label $s$. That, by the way, is why I labeled the black box $C_s$.
  </p>

  <p>
    (Incidentally, the search black box $C_s$ is sometimes called a
    search <em>oracle</em>, since it&rsquo;s this oracular thing which tells
    us whether we have a solution to the search problem or not. I use
    the term black box in this essay, but many people use the term
    &ldquo;oracle&rdquo;, and it&rsquo;s worth being aware of both terms.)
  </p>


  <div class="review_set">
    <div class="card" data-cardid="Ewwo3zBaiVT6Iad58g7f">
      <div class="question">What do we assume is the starting state
      for the quantum search algorithm?
      </div>
      <div class="answer">The all $|0\rangle$ state
      </div>
    </div>

    <div class="card" data-cardid="7g7n8V93dkRrkHbifEgP">
      <div class="question">How is the search function $s(x)$ defined?
      </div>
      <div class="answer">$s(x) = 0$ if $x$ is not a solution to the
      search problem, and $s(x) = 1$ if $x$ is a solution.
      </div>
    </div>

    <div class="card" data-cardid="dGDB7nJEfFe5lAWjgHMN">
      <div class="question">If $C_s$ is the search black box and
      $s(x)$ is the search function, what is $C_s|x\rangle|0\rangle$?
      </div>
      <div class="answer">$|x\rangle|s(x)\rangle$
      </div>
    </div>


    <div class="card" data-cardid="L99xdeOukJcg3irAJBPV">
      <div class="question">The search black box $C_s$ is sometimes
      also known as an ___.
      </div>
      <div class="answer">oracle
      </div>
    </div>

    <div class="card" data-cardid="1nhHP6NkcpuUR0fhuBsv">
      <div class="question">In the case of the travelling salesperson
      problem, what is $C_s|x\rangle|0\rangle$ when the route $x$ has
      length less than the threshold $T$?
      </div>
      <div class="answer">$|x\rangle|1\rangle$
      </div>
    </div>

  </div>
  
  <p>
    <strong>Getting a clean black box:</strong> Earlier, I blithely
    asserted you can take a classical circuit for computing the search
    function $s(x)$, and turn it into a quantum circuit which has the
    effect $C_s|x\rangle|0\rangle = |x\rangle|s(x)\rangle$.
  </p>

  <p>
    Actually, there&rsquo;s a slight complication. To illustrate the issue
    concretely, suppose you&rsquo;re trying to compute $s(x) = x_1 \wedge
    x_2 \wedge x_3$, that is, the AND of three bits (corresponding to
    a search solution $s = 111$, in binary).  To do this, we&rsquo;d start
    by using a Toffoli gate to compute the AND of the first two bits,
    $x_1 \wedge x_2$:
  </p>

  <p>
    <center>
      <img src="/assets2/toffoli_and.png" class="RetinaImage">
    </center>
  </p>

  <p>
    Then we&rsquo;d use another Toffoli gate to AND the result with $x_3$:
  </p>

  <p>
    <center>
      <img src="/assets2/double_and.png" class="RetinaImage">
    </center>
  </p>

  <p>
    So we&rsquo;ve indeed computed $s(x) = x_1 \wedge x_2 \wedge x_3$, but
    along the way we&rsquo;ve also generated an intermediate working qubit
    in the state $|x_1 \wedge x_2\rangle$. That working state wasn&rsquo;t
    part of our original specification. Put another way, we wanted to
    compute
  </p>

  <p>
    $$|x_1, x_2, x_3\rangle|0\rangle \rightarrow |x_1, x_2,
    x_3\rangle|x_1 \wedge x_2 \wedge x_3\rangle,$$
  </p>

  <p>
    and instead we ended up computing
  </p>

  <p>
    $$|x_1, x_2, x_3\rangle|0\rangle|0\rangle \rightarrow |x_1, x_2,
    x_3\rangle|x_1\wedge x_2\rangle |x_1 \wedge x_2 \wedge x_3\rangle.$$
  </p>

  <p>
    More generally, suppose we try to convert a classical circuit
    computing the search function $s(x)$ into a quantum circuit. If we
    do it using the recipe described in the last essay &ndash;
    converting AND gates to Toffoli gates, and classical NOT gates to
    quantum NOT gates &ndash; it won&rsquo;t take $|x\rangle|0\rangle$
    to $|x\rangle|s(x)\rangle$. There will be extra qubits involved,
    arising as intermediaries during the computation. The result will
    be something more like
  </p>

  <p>
    $$|x\rangle|0\rangle|0\rangle \rightarrow |x\rangle|s(x)\rangle |w(x)\rangle,$$
  </p>

  <p>
    where the extra register is a supply of one-or-more working
    qubits, and they end up in some state $|w(x)\rangle$ produced
    along the way.
  </p>

  <p>
    The difference might seem small. We&rsquo;re certainly close to having
    our search black box. But it turns out to be crucial to the
    quantum search algorithm that we get that clean behavior,
    $|x\rangle|0\rangle \rightarrow |x\rangle|s(x)\rangle$. We&rsquo;ll
    discuss later why this clean form for the computation is
    needed. For right now, though, let&rsquo;s figure out how to do it.
  </p>

  <p>
    Fortunately, there&rsquo;s a simple trick called <em>uncomputation</em>
    which works. It involves three steps. The first is more or less
    what you&rsquo;d expect, but the second and third are quite clever:
  </p>

  <ol>
    <li>Compute $|x\rangle |0\rangle |0\rangle \rightarrow
      |x\rangle|s(x)\rangle |w(x)\rangle$, using the standard approach
      of converting classical AND gates to Toffoli gates, and
      classical NOT gates to quantum NOT gates.</li>

    <li> Add on an extra qubit in the $|0\rangle$ state, and do a CNOT
    with $|s(x)\rangle$ as the control. This effectively copies the
    result, and we obtain:
    $|x\rangle|s(x)\rangle|w(x)\rangle|s(x)\rangle$.
    </li>

    <li>Now apply all the gates from step 1, but in reverse order, and
      applying at each step the inverse gate.  The result is to undo or 
      <em>uncompute</em> what happened in step 1, resulting in
      $|x\rangle|0\rangle|0\rangle|s(x)\rangle$.</li>
  </ol>

  <p>
    At the end, we can ignore the $|0\rangle|0\rangle$ state, which
    isn&rsquo;t changed at all by the entire process. And so the net result
    of these steps is the desired transformation, $|x\rangle|0\rangle
    \rightarrow |x\rangle|s(x)\rangle$.
  </p>

  <p>
    Summing up, if we have a classical circuit to compute a function
    $s(\cdot)$, you can think of the three stages in the corresponding
    clean quantum circuit as: compute $s(\cdot)$, by converting
    classical gates to quantum; copy the answer using a CNOT;
    uncompute, by reversing the gates and inverting them.
  </p>

  <p>
    So, for instance, it&rsquo;s easy to convert a computation of $s(x) =
    x_1 \wedge x_2 \wedge x_3$ into the clean form using
    uncomputation. We just literally follow the steps above, and
    remember that the inverse of a Toffoli gate is a Toffoli gate:
  </p>

  <p>
    <center>
      <img src="/assets2/clean_double_and.png" class="RetinaImage">
    </center>
  </p>

  <p>
    I&rsquo;ve written the results of the clean computation as
    $|x\rangle|0\rangle \rightarrow |x\rangle|s(x)\rangle$. What would
    have happened if the second register had been in the state $1$
    (or, more generally, an unknown state $z = 0$ or $1$), instead of
    $0$? You can easily trace through the above steps to see that the
    net result is
  </p>

  <p>
    $$|x\rangle|z\rangle \rightarrow |x\rangle|z \oplus s(x) \rangle,$$
  </p>

  <p>
    where the addition is done modulo $2$. This type of clean
    computation turns out to be useful in many quantum computations,
    not just quantum search, and the form just shown is the standard
    form in which it is presented. In particular, we can do a clean
    computation of any function $f(x)$ for which we have a classical
    circuit, not just search functions. In any case, we will assume
    that the form given in the equation just above is the effect of
    the search black box $C_s$.
  </p>

  <p>
    It&rsquo;s worth noting that there is a price to pay in converting a
    classical circuit to its equivalent clean form: the uncomputation
    step doubles the number of gates required, and the copying step
    adds an extra CNOT on top of that doubling. So there is a genuine
    overhead in getting to the clean form. Still, for the speedup
    we&rsquo;ll get from the quantum search algorithm this is a tiny price
    to pay.
  </p>

  <div class="exercises">
  <p>
    <strong>Exercise:</strong> Find a quantum circuit which computes
    $|x_1, x_2\rangle|0\rangle \rightarrow |x_1, x_2\rangle |x_1 \vee
    x_2\rangle$, where $\vee$ denotes the logical OR.
  </p>
  
  <p>
    <strong>Exercise:</strong> Find a quantum circuit which performs a
    clean computation of the classical function $s(x_1, x_2, x_3) =
    x_1 \vee x_2 \vee x_3$.
  </p>
  </div>

  <p>
    Let me finish the discussion of clean computation by introducing
    some extra pieces of quantum circuit notation that will come in
    handy later.  The notation I&rsquo;ll introduce generalizes the
    CNOT and Toffoli gates to involve more control qubits. For
    instance, here&rsquo;s an example involving three control qubits:
  </p>

  <p>
    <center>
      <img src="/assets2/c3not.png" class="RetinaImage">
    </center>
  </p>

  <p>
    It behaves as you&rsquo;d expect, NOTting the target qubit when all
    three control qubits are set, and otherwise leaving it alone. We
    just saw how to implement this using Toffoli gates and
    uncomputation:
  </p>

  <p>
    <center>
      <img src="/assets2/c3not_circuit.png" class="RetinaImage">
    </center>
  </p>

  <p>
    If we want to break this down even further, we can use techniques
    from the <a href="/qcvc#Toffoli_synthesis">last essay</a> to break
    the Toffoli gates into one- and two-qubit quantum gates.
  </p>

  <p>
    Very similar ideas can be used to synthesize even more complicated
    controlled gates, e.g. gates controlled by four qubits such as:
  </p>

  <p>
    <center>
      <img src="/assets2/c4not.png" class="RetinaImage">
    </center>
  </p>

  <p>
    In this notation, an open circle on a control qubit means gates
    are applied conditional on those control qubits being set to $0$.
    In this case, it means the NOT on the target qubit is applied
    conditional on the first two qubits being set to $0$ and the third
    and fourth being set to $1$.  I&rsquo;ll leave it to you to figure out
    the details of how to break this down into Toffoli and other
    standard quantum gates &ndash; it&rsquo;s a good exercise in applying
    the ideas we&rsquo;ve been learning.
  </p>

  <div class="exercises">
  <p>
  <strong>Exercise:</strong> Find a way of breaking the controlled
  gate shown just above (with four control qubits) down into Toffoli
  and one- and two-qubit quantum gates.
  </p>
  </div>
  
  <div class="review_set">
    
    <div class="card" data-cardid="xa7rb2oMvMMQBoz63CC0">
      <div class="question">What is the effect of the search black box
      on a general starting state, $C_s|x\rangle|z\rangle$?
      </div>
      <div class="answer">$C_s|x\rangle|z\rangle = |x\rangle|z\oplus
      s(x)\rangle$
      </div>
    </div>

    <div class="card" data-cardid="rODG1R51JooPEbJrjQ3A">
      <div class="question">What is the name of the trick used to
      ensure that the search black box is performed in a clean manner?
      </div>
      <div class="answer">uncomputation
      </div>
    </div>

    <div class="card" data-cardid="Ea789u5S0nn62OfOgPCz">
      <div class="question">How many steps are involved in converting
	a classical circuit for a function $s$ into a clean
	computation of that function?</div>
      <div class="answer">$3$
      </div>
    </div>

    <div class="card" data-cardid="2ovwGTmhgbIh2dN533TZ">
      <div class="question">Briefly, what are the three steps in a
      clean computation of a function $s$ for which we have a
      classical circuit?</div>
      <div class="answer">Compute $s$, by converting classical gates
      to quantum; copy the answer using a CNOT; uncompute, by
      reversing the gates and inverting them.
      </div>
    </div>

    <div class="card" data-cardid="5IXVgDAZTk9AWVhKQ70i">
      <div class="question">![](/assets2/c4not-card.png#FloatRight)What&rsquo;s this quantum gate
	represent?
      </div>
      <div class="answer">A gate with four control qubits, where the
      NOT is applied to the target when the first two controls are $0$
      and the final two controls are $1$.
      </div>
    </div>

  </div>
    
  <p>
    <strong>Database search?</strong> The quantum search algorithm is
    sometimes described as a <em>database</em> search algorithm. This
    is often done in popular media accounts, and sometimes even in
    research papers. Unfortunately, it&rsquo;s not a terribly helpful way of
    thinking about it. For one thing, databases are usually ordered,
    and that ordering makes them extremely fast to search. For
    instance, suppose you have an alphabetically ordered list of
    surnames:
  </p>

  <blockquote>
    Calder<br>
    Davies<br>
    Jones<br>
    Ng<br>
    Prothero<br>
    Richards<br>
    &hellip;
  </blockquote>

  <p>
    To find out if a name is on the list you wouldn&rsquo;t run through the
    entire list. Rather, you&rsquo;d exploit the ordering to do some kind of
    binary search. The result is that instead of needing to examine
    the database $N$ times, you only need to examine it on the order
    of $\log_2(N)$ times. That&rsquo;s vastly faster than the order
    $\sqrt{N}$ times required by the quantum search algorithm.  If
    someone needs to examine a database $N$ times in order to search
    it, it probably means they need to think harder about how they&rsquo;re
    indexing their database.
  </p>

  <p>
    Why is the notion of a quantum database search used so often in
    explanations? My guess is that it&rsquo;s because searching a database
    is the most obvious really concrete way of thinking about
    search. But it&rsquo;s that very concreteness which makes it easy to
    build database indices, which usually make database search a
    trivial problem in practice. Search is vastly more challenging
    when it&rsquo;s hard to find or exploit any structure in the search
    space, in problems like decoding a code or the TSP or protein
    folding. It&rsquo;s in such cases that the quantum search algorithm will
    shine.  More precisely: the quantum search algorithm is useful
    when: (a) you&rsquo;re doing a search where there&rsquo;s little exploitable
    structure in the search space; but (b) you have an algorithm which
    lets you recognize solutions to the search problem, and so you can
    build the search black box.
  </p>

  
  
  <div class="review_set">

    <div class="card" data-cardid="1tjVhaHk7yUz4K3vZvij">
      <div class="question">If you have an alphabetically ordered
      database of $N$ names, roughly how many times do you need to
      examine the database to find a particular name, using binary
      search?
      </div>
      <div class="answer">$\log_2(N)$
      </div>
    </div>

    <div class="card" data-cardid="OoydyiwqWmFaEHxnBhZ9" data-questionadjustments="1">
      <div class="question">Suppose you tried to use the quantum
      search algorithm to search an alphabetically ordered database of
      $N$ names. Would the roughly $\pi\sqrt{N}/4$ examinations
      required be more or less than the $\log_2(N)$ required
      classically?
      </div>
      <div class="answer">Far more
      </div>
    </div>

    <div class="card" data-cardid="yStQ0ljmIkzSoHMPk2Uy">
      <div class="question">Why is the quantum search algorithm
      unlikely to be much use in searching databases?
      </div>
      <div class="answer">Because well-designed databases are often
      ordered using indices, and you can search an ordered database
      much faster than quantum search operates.
      </div>
    </div>

  </div>
  
    <h2 id="details-of-the-quantum-search-algorithm">Details of the quantum search algorithm</h2>

  <p>
    Now that we have an overall picture, what quantum circuits
    actually make the quantum search algorithm work?  Rather than
    simply present the final algorithm, I&rsquo;m going to describe a line
    of thinking you might imagine using to discover the quantum search
    algorithm. That means we&rsquo;ll be making guesses, and occasionally
    backtracking as we realize something doesn&rsquo;t work. It has the
    disadvantage that the presentation is longer than if I just showed
    you the final algorithm. But it also makes it easier to understand
    where the quantum search algorithm comes from, and why it
    works. It&rsquo;s often surprisingly instructive to see reasonable ideas
    tried (and fail), and how it&rsquo;s possible to learn from those
    failures.
  </p>

  <p>
    Now, we&rsquo;re looking for a truly quantum algorithm, one that
    exploits quantum mechanics to operate faster than a classical
    computer.  So even though we start in a computational basis state,
    we should quickly move out of that state. After all, if we stayed
    in the computational basis we could do everything on a classical
    computer, and there would be no possibility of an advantage for a
    quantum computer.
  </p>

  <p>
    What state might we move into?
  </p>

  <p>
    In our circuit model, one of the gates that produces non-classical
    states is the Hadamard gate. Remember that the Hadamard gate takes
    the state $|0\rangle$ to $\frac{|0\rangle+|1\rangle}{\sqrt 2}$ and
    $|1\rangle$ to $\frac{|0\rangle-|1\rangle}{\sqrt 2}$.  A nice
    thing about the state $\frac{|0\rangle+|1\rangle}{\sqrt 2}$ is
    that it&rsquo;s a truly quantum state which is as agnostic as it&rsquo;s
    possible to be about the value of the bit. Suppose we applied a
    Hadamard gate to all the $|0\rangle$&rsquo;s at the start:
  </p>

  <p>
    <center><img src="/assets2/hadamard.png" class="RetinaImage"></center>
  </p>

  <p>
    Then we&rsquo;d end up with the quantum state
  </p>

  <p>
    $$
    \frac{\sum_{x_1, x_2, \ldots, x_n = 0, 1} |x_1, x_2, \ldots, x_n\rangle}{\sqrt 2^n},
    $$
  </p>

  <p>
    where we sum over both $0$ and $1$ for each qubit. Put another
    way, we end up with an equal superposition of all possible
    solutions to the search problem. It&rsquo;s a starting state that&rsquo;s
    completely agnostic about the solution.  We can write this more
    compactly by setting $N := 2^n$ to be the size of the search
    space, and writing the last state as an equal superposition over
    all possible search solutions,
  </p>

  <p>
    $$
    \frac{\sum_x |x\rangle}{\sqrt{N}}.
    $$
  </p>

  <p>
    This state will appear often in what follows, and it&rsquo;s helpful to
    have some notation for it: we&rsquo;ll call it $|E\rangle$,
    for <em>e</em>qual superposition of possible solutions, $|E\rangle
    := \sum_x |x\rangle/\sqrt{N}$.
  </p>
    
  <p>
    Of course, this is just a guess as to how we might start out.  In
    fact &ndash; spoiler alert! &ndash; we&rsquo;ll eventually find that
    starting in pretty much any superposition state works. But the
    equal superposition $|E\rangle$ is easy to prepare, and turns out
    to work well. It&rsquo;s got the additional bonus that this state turns
    up in lots of quantum algorithms, so it&rsquo;s good to get comfortable
    with it.
  </p>

  <p>
    By the way, I said above that $N = 2^n$ is the size of the search
    space. This isn&rsquo;t always literally true. For instance, if we&rsquo;re
    using $x = x_1 x_2 \ldots$ to describe routes in the traveling
    salesperson problem, it might be that some bit strings don&rsquo;t
    represent valid routes, so the actual size of the search space may
    be smaller than $2^n$. I won&rsquo;t consider that possibility in any
    detail, although the algorithm we&rsquo;ll find is easily modified to
    cope with that possibility.
  </p>

  <p>
    Now, suppose we introduce a check qubit and apply the search black
    box to our equal superposition state.  We get the state:
  </p>

  <p>
    $$
    \sum_x \frac{|x\rangle|s(x)\rangle}{\sqrt{N}}.
    $$
  </p>

  <p>
    That doesn&rsquo;t immediately help much: if we were to do a measurement
    in the computational basis, we get a result $x, s(x)$ where $s(x)
    = 1$ (i.e., the solution to the search problem) with probability
    $1/N$. We&rsquo;re essentially just guessing a solution.
  </p>

  <p>
    What could we do instead if not a measurement? The most obvious
    thing is to apply the search black box again. Unfortunately, this
    adds $s(x)$ to itself, modulo $2$, and so we end up in the state:
  </p>

  <p>
    $$
    \sum_x \frac{|x\rangle|0\rangle}{\sqrt N}.
    $$
  </p>

  <p>
    This isn&rsquo;t progress &ndash; we&rsquo;re back where we were earlier!
  </p>

  <p>
    Another thing to try is using a new check qubit.  The simplest
    thing would be to apply the search black box over and over, each
    time with a new check qubit, so you end up with the state:
  </p>
  
  <p>
    $$
    \sum_x \frac{|x\rangle|s(x)\rangle|s(x)\rangle \ldots}{\sqrt N}.
    $$
  </p>

  <p>
    Again, this doesn&rsquo;t seem all that promising. If you measured in
    the computational basis you&rsquo;d again get a solution with
    probability $1/N$, which is too low to be useful.
  </p>

  <p>
    What we want is to somehow increase the amplitudes in the terms
    with a $1$ in the check qubit, and decrease the amplitudes when
    there is a $0$ in the check qubit, a way of concentrating the
    amplitude in the right place. Imagine, for instance, we could do
    the following: if the check bit is $0$, then shrink the amplitude
    of the term by a factor $2$. And if the check bit is $1$, then
    double the amplitude by a factor $2$.
  </p>

  <p>
    Actually, that can&rsquo;t work &ndash; the state would quickly become
    unnormalized. But maybe something like this could work, shrinking
    the &ldquo;bad&rdquo; amplitudes and growing the
    &rdquo;good&rdquo; amplitudes, balancing things so state
    normalization is preserved.
  </p>

  <p>
    Unfortunately, this isn&rsquo;t possible either, at least not directly!
    The trouble is that quantum gates are linear. That means they
    don&rsquo;t directly &ldquo;see&rdquo; the amplitudes at all. For
    instance, for any gate described by a unitary matrix $U$ and
    superposition of states,
  </p>

  <p>
    $$
    U (\alpha|\psi\rangle+\beta|\phi\rangle) = \alpha U|\psi\rangle + \beta U|\phi\rangle.
    $$
  </p>

  <p>
    That is, the gate doesn&rsquo;t directly respond to the values of the
    amplitudes $\alpha$ and $\beta$ at all, and so there&rsquo;s no
    shrinking or growing of amplitudes.
  </p>

  <div class="review_set">
    <div class="card" data-cardid="SyXs4N6ZuLgmtTyC5cky">
      <div class="question">Write an expression for the equal
      superposition state, $|E\rangle$, in the bra-ket notation.
      </div>
      <div class="answer">$|E\rangle = \frac{\sum_x |x\rangle}{\sqrt{N}}$
      </div>
    </div>

    <div class="card" data-cardid="EFsIGuNdGCWJXHYzz6DR">
      <div class="question">What&rsquo;s a quantum circuit that enables us
	to prepare the equal superposition state $|E\rangle$ for a system of
	$n$ qubits?
      </div>
      <div class="answer">
	![](/assets2/hadamard.png#FillBox)
      </div>
    </div>
  </div>

  <p>
    Well, we haven't made much progress! Since we haven&rsquo;t gotten
    very far with algebra, let&rsquo;s instead try to visualize what
    we&rsquo;re hoping for geometrically. We can think of ourselves as
    starting out in a state $|E\rangle$, and somehow trying to swing
    around to the solution $|s\rangle$, perhaps passing through some
    intermediate states $|\psi\rangle$ along the way:
  </p>

  <p>
    <center>
      <img src="/assets2/geometric_hope.png" class="RetinaImage">
    </center>
  </p>
  
  <p>
    Of course, if only we <em>knew</em> the identity $s$ of the search
    solution, we could simply swing around directly. Indeed, we could
    solve the problem in just a single step! But we don&rsquo;t know
    $|s\rangle$. Instead, we&rsquo;re hoping to use the search black box to
    somehow move closer.
  </p>

  <p>
    I want to draw your attention to one particular feature of the
    above diagram.  I&rsquo;ve shown $|E\rangle$ and $|s\rangle$ as being
    nearly orthogonal. That&rsquo;s actually a pretty accurate
    representation of reality, since no matter what the value of
    $|s\rangle$, its amplitude in the equal superposition $|E\rangle$
    is $1/\sqrt{N}$. It&rsquo;ll be useful later to have a name for the
    corresponding angle, so let me draw it here:
  </p>

  <p>
    <center>
      <img src="/assets2/delta_definition.png" class="RetinaImage">
    </center>
  </p>

  <p>
    In particular, observe that the component of $|E\rangle$ in the
    $|s\rangle$ direction is just $\sin(\Delta) = 1/\sqrt{N}$, and so
    $\Delta = \arcsin(1/\sqrt{N}) \approx 1/\sqrt{N}$.
    
  <p>
    As an aside, I&rsquo;ll be expressing all angles in radians, not
    degrees. So a right angle is $\pi/2$, a half rotation is $\pi$, a
    full rotation is $2\pi$, and so on. I know some people prefer to
    think about angles in degrees, and using radians may frustrate
    them. On the other hand, if I worked in degrees, that&rsquo;d be equally
    frustrating for people who prefer radians. Actually, it&rsquo;d be more
    frustrating (and make the presentation more complex), because
    certain facts about trigonometry are simpler when angles are
    expressed in radians. An example, which I used in the last
    paragraph, is that $\arcsin(x) \approx x$ for small $x$. That
    becomes the much uglier $\arcsin(x) \approx 180\, x/\pi$ if we
    work in degrees. So it&rsquo;s better just to work in radians. End of
    aside.
  </p>
  
  <p>
    At this point, I&rsquo;m going to engage in some <em>deus ex
    machina</em>, and ask a question: what if we could
    somehow <em>reflect</em> about the solution vector $|s\rangle$?
  </p>

  <p>
    In fact, that turns out to be possible, and I&rsquo;ll show you
    in a bit how to do it. For now though let&rsquo;s just assume we
    can do it. Here&rsquo;s what happens:
  </p>

  <p>
    <center>
      <img src="/assets2/after_reflection.png" class="RetinaImage">
    </center>
  </p>

  <p>
    In this diagram, $\theta$ is the angle between
    $|\psi\rangle$ and $|s\rangle$, so $2\theta$ is the total
    angle between $|\psi\rangle$ and its reflection.
  </p>
    
  <p>
    You may recall from elementary plane geometry that if we do two
    consecutive reflections of the plane about different axes, the net
    result is a rotation of the plane.  That seems encouraging.  The
    obvious other vector to try reflecting about is the equal
    superposition $|E\rangle$. It seems plausible that if we could
    reflect about $|s\rangle$ then we could also figure out how to
    reflect about $|E\rangle$.  For now let&rsquo;s just assume we can. The
    result is:
  </p>

  <p>
    <center>
      <img src="/assets2/double_reflection.png" class="RetinaImage">
    </center>
  </p>

  <p>
    We can see from the above diagram that we&rsquo;ve rotated from the
    original $|\psi\rangle$ by an angle $2\theta + 2\phi$, where
    $\phi$ is the angle between the equal superposition $|E\rangle$
    and the vector $|\psi\rangle$.
  </p>

  <p>
    Looking at the diagram, after the two reflections the quantum
    state is pointing in almost the opposite direction to where we
    started, i.e., it&rsquo;s close to $-|\psi\rangle$, but with a slight
    extra rotation. To see why this is true, imagine you&rsquo;re in a
    plane, and reflect a vector about two exactly orthogonal axes
    &ndash; say, the usual $x$ and $y$ axes. Of course, the result is
    just that the vector ends up pointing in the opposite direction.
  </p>

  <p>
    In this case, we&rsquo;re not reflecting about exactly orthogonal
    axes, but rather about two almost-orthogonal axes. So we&rsquo;d
    expect the net rotation to be approximately $\pi$, but with a
    small deviation. What&rsquo;s more, we&rsquo;d expect that
    deviation to be related to the angle $\Delta$ by which the axes
    failed to be orthogonal.  And that&rsquo;s exactly right: we have
    $\Delta = \frac{\pi}{2}-\theta-\phi$, and so a little algebra
    shows that the rotation is $2\theta + 2\phi = \pi-2\Delta$.
  </p>

  <p>
    This rotation of $\pi-2\Delta$ is almost what we&rsquo;re looking
    for. One thing that makes it a little hard to think about is the
    $\pi$. In fact, a rotation by $\pi$ just flips a vector in the
    plane back and forth about the origin, effectively multiplying it
    by $-1$. But in the <a href="/qcvc#global_phase">previous
    essay</a> we saw that such global phase factors make no difference
    whatsoever to outcomes at the end of a quantum computation.  So
    after the double reflection it's exactly as though we're working
    with the state $|\psi'\rangle$ shown
    below<span class="MarginNote">Ignoring such global phase factors
    sometimes bother people getting into quantum computing. If it bugs
    you, just insert a single-qubit gate $-I$ on one of the
    qubits.</span>:
  </p>

  <p>
    <center>
      <img src="/assets2/after_grover.png" class="RetinaImage">
    </center>
  </p>

  <p>
    We can now see what&rsquo;s going on very clearly: flipping about
    $|s\rangle$ and then $|E\rangle$ is the same as doing a rotation
    by $2\Delta$ (up to the global phase factor, which can be
    ignored). Summing up the result in one diagram, and omitting the
    intermediate states we have:
  </p>

  <p>
    <center>
      <img src="/assets2/grover_result.png" class="RetinaImage">
    </center>
  </p>
  
  <p>
    This is exciting news! It means we have a way of rotating from the
    starting state $|E\rangle$ an angle $2 \Delta$ closer to the
    search solution $|s\rangle$. What&rsquo;s more, we can just keep
    repeating this operation.  Maybe if we repeat it enough times we
    can rotate close to $|s\rangle$?
  </p>

  <p>
    How many times do we need to rotate to get close to $|s\rangle$?
    And how close can we get?
  </p>

  <p>
    Well, we&rsquo;re rotating each time by $2\Delta$, and ideally we&rsquo;d like
    to rotate by a total angle of $\pi/2-\Delta$. To get as close as
    possible to that total angle, the number of times we should rotate
    is just the integer closest to the ratio of the total angle
    $\pi/2-\Delta$ with the angle of each rotation $2\Delta$, i.e.:
  </p>

  <p>
    $$ \text{round}\left( \frac{\pi}{4 \Delta} - 1/2 \right) $$
  </p>

  <p>
    When we do so, we end up within an angle $\Delta$ of
    $|s\rangle$. Remember that $\Delta$ is small, so we&rsquo;re very near
    the state $|s\rangle$. It should be plausible that if you measure
    the quantum system you&rsquo;ll get the result $s$ with pretty high
    probability.  We&rsquo;ll figure out just how high that probability is
    shortly, but intuitively the overall picture is encouraging.
  </p>

  <p>
    The expression above for the number of times to do the rotation
    has many details in it, which makes it hard to think about. The
    key behavior to focus on is that the number of rotations required
    scales with $1/\Delta$. But we saw earlier that $\Delta \approx
    1/\sqrt{N}$, so $1/\Delta$ scales with $\sqrt{N}$. The result is
    that if you perform roughly $\pi \sqrt{N}/4$ rotations, you&rsquo;ll end
    up very near to the desired search solution.
  </p>

  <p>
    (By the way, I&rsquo;ve used the phrase &ldquo;roughly&rdquo;
    there because to get $\Delta \approx 1/\sqrt{N}$ we used the
    approximation $\arcsin(x) \approx x$ for small $x$. In fact, a bit
    of fiddling around with trigonometry and algebra shows that more
    than $\pi \sqrt{N}/4 + 1$ rotations are never required. In
    practice, you&rsquo;d use the exact formula with the arcsine in
    it. But that formula is a little complicated and somewhat opaque
    &ndash; the kind of thing you&rsquo;d be unlikely to memorize, but
    would instead look up, unless for some reason you needed it
    often. On the other hand, $\pi \sqrt{N}/4$ is a good shorthand,
    capturing the essential behavior, and worth remembering, along
    with the caveat that the actual expression is a little more
    complex.)
  </p>
    
  <p>
    That&rsquo;s the essence of the quantum search algorithm! There
    are still details to be filled in, but the basic outline
    is as follows:
  </p>

  <ol>
    <li>Starting in the all-$|0\rangle$ state, apply a
    Hadamard gate to each qubit to enter the equal
    superposition state $|E\rangle = \frac{\sum_x
    |x\rangle}{\sqrt N}$.</li>

    <li>Repeat the following steps, known as the Grover iteration, a
    number of times equal to:
    $$\text{round}(\pi/4\arcsin(1/\sqrt{N})-1/2) \approx
    \pi\frac{\sqrt{N}}{4}$$

      <ul>
	<li>Reflect about the state $|s\rangle$;
	</li>
	<li>Reflect about the state $|E\rangle$;</li>
      </ul>
    </li>

    <li>Measure to obtain the search solution $s$ with high
      probability.
    </li>
  </ol>

  <p>
    When you consider the remarkable feat this algorithm accomplishes
    &ndash; searching an $N$-item search using $\sim\sqrt{N}$
    examinations of that search space(!)  &ndash; this is really quite
    simple and beautiful.
  </p>

  <p>
    The algorithm is due to Lov Grover, who introduced it in 1996, and
    it&rsquo;s often called Grover&rsquo;s quantum search algorithm in his
    honor. And, as mentioned above, the two steps at the core of the
    algorithm are sometimes called the <em>Grover iteration</em>.
  </p>

  <p>
    Before filling in the remaining details in the quantum search
    algorithm, let&rsquo;s go through a few more spaced-repetition
    questions. These will help you remember many of the core elements
    of the algorithm. Note that a few details of the algorithm are
    still to be filled in, and we&rsquo;ll discuss those in later
    sections. But we've got the core ideas now.
  </p>

  <div class="exercises">
  <p>
    <strong>Exercise:</strong> At several points in this essay I ask
    you to ignore global phase factors.  If that makes you
    uncomfortable, I invite you to repeat the analysis at each place
    I&rsquo;ve made the request, not ignoring global phase
    factors. Show that the states output from the computation only
    ever change by a factor $-1$, raised to some power, and argue that
    measurement probabilities for the computation are not changed at
    all.
  </p>
  </div>

  <div class="review_set">

    <div class="card" data-cardid="L3sPnC26KledTfSry59N">
      <div class="question">Who discovered the quantum search
      algorithm?
      </div>
      <div class="answer">Lov Grover
      </div>
    </div>

    <div class="card" data-cardid="WefYondBe1O4tiEVkGPj">
      <div class="question">How many broad steps are there in
      a single Grover iteration?
      </div>
      <div class="answer">Two
      </div>
    </div>

    <div class="card" data-cardid="r2BfjOYUawb9DCFszH9Z">
      <div class="question">Two reflections about different axes in a
      plane generate a ___ of the plane.
      </div>
      <div class="answer">rotation
      </div>
    </div>

    <div class="card" data-cardid="WZs6EP3bIWBIlea9uqUC">
      <div class="question">What are the two broad steps in a
      Grover iteration?
      </div>
      <div class="answer">(1) Reflect about the search solution state
      $|s\rangle$; and (2) reflect about the equal superposition state
      $|E\rangle$.
      </div>
    </div>
    
    <div class="card" data-cardid="9DmZh6PDs9PwQ0YGEsND">
      <div class="question">The effect of the Grover iteration
      is to ___ the quantum state a little closer to the
      desired search solution $|s\rangle$.
      </div>
      <div class="answer">rotate
      </div>
    </div>

    <div class="card" data-cardid="H5nwmclaDuyqpLdN0Zq7" data-questionadjustments="1">
    <div class="question">![](/assets2/delta_definition.png#FloatRight)How is the rotation caused by the Grover
iteration related to the angle $\Delta$ shown below?
      </div>
      <div class="answer">It&rsquo;s $2 \Delta$
      </div>
    </div>

    <div class="card" data-cardid="1Lr9W17vhOD8XXm32yYL">
      <div class="question">At the conclusion of Grover&rsquo;s algorithm,
      the quantum state is guaranteed to be within an angle ___ of the
      state $|s\rangle$.
      </div>
      <div class="answer">$\Delta$
      </div>
    </div>

    <div class="card" data-cardid="RbcWb9rv7eOZt4PSYfDr">
      <div class="question">Roughly how many times do we need to
      repeat the Grover iteration in order to get near to the state
      $|s\rangle$?
      </div>
      <div class="answer">$\pi\sqrt{N}/4$
      </div>
    </div>

    
  </div>

  <h2 id="how-to-reflect-about-the-s-and-E-states">How to reflect about the $|s\rangle$ and $|E\rangle$ states?</h2>

  <p>
    How should we achieve the desired reflections about the
    $|s\rangle$ and $|E\rangle$ states? To answer this
    question, we&rsquo;ll start by focusing on the $|s\rangle$
    state, since computational basis states are closer to our
    everyday way of thinking about the world. And to make it
    even more concrete, let&rsquo;s focus on the all $0$ state,
    $|00\ldots 0\rangle$.
  </p>

  <p>
    What would such a reflection actually do?  It would mean leaving
    the $|00\ldots 0\rangle$ state alone, and taking every other
    computational basis state $|x\rangle$ to $-|x\rangle$. In terms of
    pseudocode, if the input state is $|x\rangle$:
  </p>

<pre>
if x == 00...0:
  do nothing
else:
  apply -1
</pre>

  <p>
    It&rsquo;s pretty easy to translate this into the quantum circuit
    model. You simply introduce an extra qubit that&rsquo;s used as a sort
    of workspace for the <code>if</code> statement (this working qubit
    is often called an <em>ancilla</em> qubit &ndash; an unusual word
    in everyday speech, but easy to remember if you notice that it&rsquo;s
    the word root for &ldquo;ancillary&rdquo;):
  </p>

  <p>
    <center>
      <img src="/assets2/basic_phase.png" class="RetinaImage">
    </center>
  </p>

  <p>
    This looks different to the pseudocode, but it&rsquo;s really just
    the quantum circuit version of the pseudocode.  The first
    controlled gate checks to see whether $x$ is equal to $00\ldots
    0$, as in the <code>if</code> condition, flipping the ancilla
    qubit to $1$ if so, and otherwise leaving it as $0$.  The $-Z$
    gate on the ancilla then does exactly what we want, doing nothing
    if the ancilla is set to $1$ (i.e., the <code>if</code> clause),
    and applying a factor $-1$ if the ancilla is set to $0$
    (the <code>else</code> clause). So the overall state is now
    $|x\rangle|1\rangle$ when $x$ is $00 \ldots 0$, and
    $-|x\rangle|0\rangle$ otherwise.
  </p>

  <p>
    We&rsquo;re almost done. The final controlled gate is there so we can
    clean up the ancilla qubit, and subsequently ignore it. To do
    that, we apply the same controlled gate again, resetting the
    ancilla to $0$, no matter what the initial computational basis
    state was. The result is the state $|x\rangle|0\rangle$ when $x$
    is $00\ldots 0$, and $-|x\rangle|0\rangle$ when $x$ is anything
    else. So no matter the value of $x$ the circuit leaves the ancilla
    in the fixed state $|0\rangle$, and that ancilla can be ignored
    through subsequent computations. Ignoring the ancilla, we see that
    the $|x\rangle$ register has been reflected about the
    $|00\ldots\rangle$ state, just as we wanted.
  </p>

  <p>
    There&rsquo;s a rough heuristic worth noting here, which is that you can
    often convert <code>if-then</code> style thinking into quantum
    circuits.  You introduce an ancilla qubit to store the outcome of
    evaluating the <code>if</code> condition. And then depending on
    the state of the ancilla, you perform the appropriate state
    manipulation. Finally, when possible you reverse the initial
    computation, resetting the ancilla to its original state so you
    can subsequently ignore it.
  </p>
    
  <p>
    For the reflection about $|00\ldots 0\rangle$ there&rsquo;s a
    clever trick which can be used to simplify the circuit shown
    above.  Instead of using an ancilla in the $|0\rangle$ state,
    start the ancilla in the $\frac{|0\rangle-|1\rangle}{\sqrt 2}$
    state (you do this using a NOT gate followed by a Hadamard gate on
    $|0\rangle$), and then use the following circuit:
  </p>

  <p>
    <center>
      <img src="/assets2/improved_phase.png" class="RetinaImage">
    </center>
  </p>

  <p>
    Why does this work?  If $x \neq 00\ldots$, then nothing happens,
    and we end up in the state
    $|x\rangle\frac{|0\rangle-|1\rangle}{\sqrt 2}$. If $x = 00\ldots$
    the ancilla qubit is NOTted, changing it from
    $\frac{|0\rangle-|1\rangle}{\sqrt 2}$ to
    $\frac{|1\rangle-|0\rangle}{\sqrt 2}$, which is, of course, just
    $-\frac{|0\rangle-|1\rangle}{\sqrt 2}$. In both cases this is
    exactly what we wanted, except for a global phase factor of $-1$,
    which we can ignore. Furthermore, no matter the value of $x$ the
    circuit leaves the ancilla in the fixed state
    $\frac{|0\rangle-|1\rangle}{\sqrt 2}$, and so the ancilla can be
    ignored through subsequent computations.
  </p>

  <p>
    This is a nice trick, which I sometimes call the &ldquo;phase
    trick&rdquo;. I must admit, it seems a little like magic. It&rsquo;s one
    of those things that&rsquo;s easy to verify works, but it&rsquo;s not so
    obvious how you would have discovered it in the first place.  I
    don&rsquo;t actually know the history of the trick (the earliest mention
    I know is in
    this <a href="https://arxiv.org/abs/quant-ph/9708016">1997
    paper</a>), but here&rsquo;s how you might have discovered it. Suppose
    you&rsquo;d been working hard on the original circuit I showed, thinking
    about each element:
  </p>

  <p>
    <center>
      <img src="/assets2/basic_phase.png" class="RetinaImage">
    </center>
  </p>

  <p>
    I don&rsquo;t necessarily mean you were trying to simplify the circuit,
    I just mean you were messing around trying to better understand
    how the circuit works. And then suppose in some other context
    someone mentioned to you (or you noticed) that
    $X\frac{|0\rangle-|1\rangle}{\sqrt 2} =
    -\frac{|0\rangle-|1\rangle}{\sqrt 2}$. If you&rsquo;d been sufficiently
    deep into thinking about the original circuit, a lightbulb might
    go on and you&rsquo;d think &ldquo;Hey, the NOT gate can be used to
    generate a factor $-1$, without otherwise changing the state of
    the qubit its being applied to. That kind of factor is just what
    we needed in our reflections. I wonder if I can somehow use that
    in my original circuit?&rdquo;
  </p>

  <p>
    Having made the connection you&rsquo;d eventually figure the
    second circuit out, though it might have required a fair bit more
    work before you got the circuit just right.
  </p>

    <div class="review_set">
    <div class="card" data-cardid="hmxdY3J3sFJL2bbWkQ56">
      <div class="question">The term ancilla qubit is a synonym for
      ___ qubit.
      </div>
      <div class="answer">working
      </div>
    </div>

    <div class="card" data-cardid="ORrpISpoT5yJVO6hpbUT">
      <div class="question">What&rsquo;s a quantum circuit that lets us
      implement the reflection about the $|00\ldots 0\rangle$ state?
      </div>
      <div class="answer">![](/assets2/improved_phase.png#FillBox)
      </div>
    </div>

    <div class="card" data-cardid="ksCaGlw1BJUsHs5HWsCZ">
      <div class="question">How can you express the reflection
      about the $|00\ldots 0\rangle$ state in pseudocode?
      </div>
      <div class="answer">
${"```"}
if x == 00...0:
  do nothing
else:
  apply -1
${"```"}
      </div>
    </div>
    </div>
    
  <p>
    <strong>Reflection about the $|s\rangle$ state:</strong> Having
    figured out how to do the reflection for the all $0$ state, it&rsquo;s
    easy to do it for the $|s\rangle$ state. We just use the search
    black box, in exactly the same style as the circuit just shown
    above:
  </p>

  <p>
    <center>
      <img src="/assets2/s_phase.png" class="RetinaImage">
    </center>
  </p>

  <p>
    It works for exactly the same reasons as the earlier circuit: the
    search black box is effectively applying a NOT gate to the
    ancilla, conditional on $x$ being equal to $s$. You&rsquo;ll
    notice, by the way, that the phase trick buys us something nice
    here. If we&rsquo;d used the original circuit, without the phase
    trick, we&rsquo;d need two applications of the search black box to
    do the reflection about $|s\rangle$. So the phase trick decreases
    the cost of the quantum search algorithm by a factor two, a nice
    win.
  </p>

  <p>
    <strong>Reflection about the equal superposition state,
    $|E\rangle$:</strong> The first time I thought about how
    to do this, I got a little paralyzed, thinking in essence:
    &ldquo;Ooh, the $|E\rangle$ state is strange and quantum,
    how could we possible reflect about it?&rdquo;
  </p>

  <p>
    Actually, it&rsquo;s straightforward: just move the $|E\rangle$
    state to $|00\ldots 0\rangle$, reflect about $|00\ldots 0\rangle$,
    and then move the $|00\ldots 0\rangle$ state back to
    $|E\rangle$. Here&rsquo;s a circuit which does it:
  </p>

  <p>
    <center>
      <img src="/assets2/e_phase.png" class="RetinaImage">
    </center>
  </p>

  <p>
    This circuit works because the product of Hadamard gates both
    moves $|00\ldots 0\rangle$ to $|E\rangle$, as we saw earlier, and
    also moves $|E\rangle$ back to $|00\ldots 0\rangle$, since the
    Hadamard gate is its own inverse. 
  </p>

  <p>
    I&rsquo;m not sure what lesson to draw from my initial fear of this
    problem, and its actual ease of solution &ndash; perhaps that
    sometimes things sound scary because they&rsquo;re unfamiliar, but in
    fact they&rsquo;re simple.
  </p>


  <div class="exercises">
  <p>
    <strong>Exercise:</strong> Prove that the circuit shown above
    does, indeed, reflect about $|E\rangle$. To do the proof, suppose
    the input to the circuit is $\alpha|E\rangle + \beta
    |E_\perp\rangle$, where $|E_\perp\rangle$ is some state orthogonal
    to $|E\rangle$. Then argue that the effect of the circuit is to
    take this to $-(\alpha|E\rangle-\beta |E_\perp\rangle)$. Up to a
    global phase factor this is the desired reflection.
  </p>
  </div>

  <div class="review_set">

    <div class="card" data-cardid="0b2a08miyI3rNHNpnpFW">
      <div class="question">What&rsquo;s a quantum circuit that
  lets us implement the reflection about the
  $|s\rangle$ state?
      </div>
      <div class="answer">![](/assets2/s_phase.png#FillBox)
      </div>
    </div>

    <div class="card" data-cardid="FM9RqbfYjninMWCIuTcR">
      <div class="question">What&rsquo;s a quantum circuit that
  lets us implement the reflection about the equal
  superposition state, $|E\rangle$?
      </div>
      <div class="answer">![](/assets2/e_phase.png#FillBox)
      </div>
    </div>

  </div>

  <h2 id="measuring-the-output">Measuring the output</h2>

  <p>
    As we saw earlier, the quantum search algorithm doesn&rsquo;t
    produce the state $|s\rangle$ exactly as output. Instead it
    produces a quantum state $|\psi\rangle$ which is within an angle
    $\Delta$ of $|s\rangle$, as shown below (in this example
    $|\psi\rangle$ has slightly over-rotated past $|s\rangle$):
  </p>

  <p>
    <center>
      <img src="/assets2/measurement_analysis.png" class="RetinaImage">
    </center>
  </p>

  <p>
    Now, the angle $\Delta$ is small (particularly for a large search
    space, i.e., large $N$, which is when we&rsquo;re most interested in
    search), which means $|\psi\rangle$ must be very close to
    $|s\rangle$. Intuitively, you&rsquo;d expect a measurement in the
    computational basis would produce $s$ with high probability.
  </p>

  <p>
    That intuition is correct.
  </p>

  <p>
    In particular, the probability a computational basis measurement
    gives the result $s$ is just the square of the amplitude for
    $|s\rangle$ in $|\psi\rangle$.  That&rsquo;s at least equal to
    $\cos^2(\Delta) = 1-\sin^2(\Delta)$, which is just $1-1/N$.
  </p>

  <p>
    Summing up: the probability that a computational basis
    state measurement gives the outcome $s$ is at least
    $1-1/N$.  
  </p>

  <p>
    So, for instance, if your search space has $N = 1,000$ or more
    elements, then the probability the search algorithm will find the
    correct outcome $s$ is at least $1-1/1000$, i.e., at least $99.9$
    percent. It works even more reliably for larger search spaces.
  </p>

  <p>
    Now, even with this high probability you might still reasonably
    worry about what happens if the measurement gives the wrong
    outcome. Fortunately, it&rsquo;s possible to quickly check whether
    that&rsquo;s happened &ndash; whatever the measurement outcome is, we
    can use the search black box to check whether it&rsquo;s a genuine
    solution or not. If it&rsquo;s not, we simply rerun the algorithm.
  </p>

  <p>
    That, in turn, creates a worry that you&rsquo;d need to rerun the
    algorithm many times. But for large $N$ &ndash; the case we
    usually care about when searching! &ndash; that&rsquo;s extremely
    unlikely. A little probability calculation shows that on average
    the number of times needed to run the algorithm is never more than
    $1/(1-1/N)$, which is very close to $1$.  Unsurprisingly, but
    pleasingly, our initial intuition was good: the quantum search
    algorithm produces the right answer, with high probability.
  </p>

  <div class="review_set">
    <div class="card" data-cardid="zGMZpjgEiDSJCLD7G98T">
      <div class="question">What&rsquo;s a lower bound on the
      probability the quantum search algorithm gives the
      correct output, $s$?
      </div>
      <div class="answer">$1-1/N$
      </div>
    </div>

    <div class="card" data-cardid="IuBQyLPoElqqOD4eVO1h">
      <div class="question">How can you check that the quantum search
      algorithm has output a true solution to the search problem?
      </div>
      <div class="answer">Check it using the search black box.
      </div>
    </div>
    
    <div class="card" data-cardid="CR1c12WNtQxoPQ56ctWP">
      <div class="question">What should you do if the quantum
      search algorithm outputs something that isn&rsquo;t the answer
      to the search problem?
      </div>
      <div class="answer">Rerun the algorithm.
      </div>
    </div>

    <div class="card" data-cardid="zz3Uazi0ARipLxXz3NIB">
      <div class="question">The probability the quantum search
      algorithm gives the correct result $s$ as output is at least
      ___.
      </div>
      <div class="answer">$1-1/N$
      </div>
    </div>

  </div>

  <h2 id="summary-of-the-quantum-search-algorithm">Summary of the quantum search algorithm</h2>

  <p>
    Let&rsquo;s sum up our completed understanding of the quantum search
    algorithm:
  </p>

  <ol>
    <li>Starting in the all-$|0\rangle$ state, apply a
    Hadamard gate to each qubit to enter the equal
    superposition state $|E\rangle = \frac{\sum_x
    |x\rangle}{\sqrt N}$.</li>

    <li>Repeat the following Grover iteration a number of times equal
    to: $$\text{round}(\pi/4\arcsin(1/\sqrt{N})-1/2) \approx
    \pi\frac{\sqrt{N}}{4}$$

      <ul>
	<li>Reflect about the state $|s\rangle$, using the circuit:
	  <p>
	    <center>
              <img src="/assets2/s_phase.png" class="RetinaImage">
	    </center>
	  </p>
	</li>

	<li>Reflect about the state $|E\rangle$, using the
	  circuit:
	  <p>
	    <center>
              <img src="/assets2/e_phase.png" class="RetinaImage">
	    </center>
	  </p>
	</li>
	
      </ul>
    </li>
    
    <li>Measure to obtain the search solution $s$ with
      probability at least $1-1/N$.</li>

    <li>Use the search black box to check whether the
      measurement outcome is truly a solution to the search
      problem. If it is, we&rsquo;re done; if not, rerun the
      algorithm.
    </li>
  </ol>

  <p>
    That&rsquo;s it, the complete quantum search algorithm!
  </p>

  <p>
    I&rsquo;ve tried to explain quantum search using what I call discovery
    fiction, a mostly-plausible series of steps you could imagine
    having taken to discover it, complete with occasional wrong turns
    and backtracking. Despite my attempts to make it legible, I
    believe there&rsquo;s still something almost shocking about the quantum
    search algorithm. It&rsquo;s incredible that you need only examine an
    $N$-item search space on the order of $\sqrt{N}$ times in order to
    find what you&rsquo;re looking for. And, from a practical point of view,
    we so often use brute search algorithms that it&rsquo;s exciting we can
    get this quadratic speedup. It seems almost like a free lunch. Of
    course, quantum computers still being theoretical, it&rsquo;s not quite
    a free lunch &ndash; more like a multi-billion dollar,
    multi-decade lunch!
  </p>

  <p>
    <strong>Variations on the basic quantum search algorithm:</strong>
    I&rsquo;ve explained the quantum search algorithm in its simplest
    form. There are many variations on these ideas. Especially useful
    variations include extending the algorithm so it can cope with the
    case of multiple solutions, and extending the algorithm so it can
    be used to estimate the number of solutions if that number isn&rsquo;t
    known in advance.
  </p>

  <p>
    I won&rsquo;t discuss these in any detail.  But if you want a challenge,
    try attacking these problems yourself. A good starting point is to
    find a search algorithm for the case where there exactly $2$
    search solutions, say $s_1$ and $s_2$.  You already have most of
    the ideas needed, but it&rsquo;s still an instructive challenge to
    figure it out. If you&rsquo;re looking for much more detail about
    variations on the quantum search algorithm, you can find it in
    Chapter 6
    of <a href="https://www.amazon.com/Quantum-Computation-Information-10th-Anniversary/dp/1107002176/">my
    book with Ike Chuang</a>.
  </p>

  <p>
    <strong>What if we used a different starting state?</strong> We
    simply guessed that the state $|E\rangle$ was a good starting
    state. Imagine we&rsquo;d started in a different quantum state, let&rsquo;s
    call it $|\phi\rangle$. And then we repeatedly reflected about
    $|s\rangle$ and about $|\phi\rangle$. As before, the net result of
    such a double reflection is a rotation, with the angle equal to
    $\pi$ minus double the angle between $|\phi\rangle$ and
    $|s\rangle$. It&rsquo;s fun to think about different things one can do
    with such a rotation.  I won&rsquo;t get into it here, except to quickly
    mention that this observation can be used to do a type of
    structured search. For instance, if we know some values $x$ aren&rsquo;t
    possible solutions to the search problem, we can actually speed
    the search algorithm up by making sure $|x\rangle$ doesn&rsquo;t appear
    in the initial superposition $|\phi\rangle$.
  </p>
  
  <p>
    <strong>Can we improve the quantum search algorithm?</strong> A
    good question to ask is whether it&rsquo;s possible to improve the
    quantum search algorithm?  That is, is it possible to find a
    quantum algorithm which requires fewer applications of the search
    black box? Maybe, for instance, we could solve the search problem
    using $\sqrt[3]{N}$ applications of the search black box. That
    would be a tremendously useful improvement.
  </p>

  <p>
    If we were truly optimistic we might even hope to solve the search
    problem using on the order of $\log(N)$ applications of the search
    black box. If that were possible, it would be revolutionary. We
    could use the resulting quantum algorithm to very rapidly solve
    problems like the traveling salesperson problem, and other
    NP-complete optimization problems, problems such as protein
    folding, satisfiability, and other famous hard problems. It would
    be a silver bullet, a way in which quantum computers were vastly
    superior to classical.
  </p>

  <p>
    Unfortunately, it turns out that the quantum search algorithm as
    I&rsquo;ve presented it is optimal. In particular, no search
    algorithm with faster than $\sim\sqrt{N}$ scaling is
    possible. This was proved in a
    remarkable <a href="https://arxiv.org/abs/quant-ph/9701001">1997
    paper</a>. While this is a real pity, the quantum search algorithm
    still provides a more limited but <em>bona fide</em> silver bullet
    for speeding up a wide class of classical computations.
  </p>

  <div class="exercises">
  <p>
    <strong>Exercise:</strong> Suppose that instead of performing a
    measurement, we instead continue performing Grover
    iterations. Argue that the quantum state will continue to rotate,
    and that we would expect the amplitude for $|s\rangle$ to start
    to <em>decrease</em>.
  </p>

  <p>
    <strong>Exercise:</strong> Argue that if we continue performing
    Grover iterations, as in the last question, we&rsquo;d eventually expect
    the measurement probability for $s$ to be no more than $1/N$.
  </p>
  </div>

  <div class="review_set">
    <div class="card" data-cardid="N4uw8zdcSTLXFpJbWYGw">
      <div class="question">What happens if we continue performing
      Grover iterations past the usual number of desired iterations?
      </div>
      <div class="answer">The amplitude for $|s\rangle$ will start to
      decrease.
      </div>
    </div>

    <div class="card" data-cardid="eXdLS1v8QTi0Cjp3mwRn">
      <div class="question">Is there a quantum search algorithm which
	requires the application of the search black box on the order
	of $\sqrt[3]{N}$ times? Why not?
      </div>
      <div class="answer">No. Because $\sim\sqrt{N}$ scaling is
      provably optimal.
      </div>
    </div>
    
  </div>

  <h2 id="what-can-we-learn-from-the-quantum-search-algorithm">What can we learn from the quantum search algorithm?</h2>

  <p>
    Are there any general lessons about quantum computers we can learn
    from the quantum search algorithm?
  </p>

  <p>
    Although dozens or hundreds of quantum algorithms have been
    developed, most are for relatively specialized &ndash; indeed,
    often rather artificial &ndash; problems. Apart from simulating
    quantum systems, only a handful of inarguably extremely useful
    quantum algorithms are known (quantum search is one). In
    particular, there is as yet no good general-purpose recipe for
    saying when a problem can be fruitfully attacked using a quantum
    computer, or how. Quantum algorithm design is still bespoke.
  </p>

  <p>
    For this reason, we should beware any too-pat explanation of why
    the quantum search algorithm works. If an explanation is really
    good, it should enable us to find interesting new algorithms, not
    merely provide eagle-eyed hindsight.
  </p>

  <p>
    With that caveat in mind, I do want to make one observation. Below
    is an animation showing the amplitudes for all the computational
    basis states as the quantum search algorithm runs. In particular,
    it shows the amplitudes after each Grover iteration, starting at
    iteration $0$ and running up to the final iteration &ndash; in
    this case, iteration number $7$:
  </p>

  <center>
  <canvas id="search_amplitudes" width="340" height="200">
  </canvas>
  </center>
  

  <p>
    You can see that the effect of the Grover iteration is to take
    amplitude from non-solutions and gradually concentrate it in the
    solution.  This is just what I said earlier couldn&rsquo;t be done
    directly. That was true, in the sense that all the gates in our
    circuit are still acting linearly through the amplitudes.
  </p>

  <p>
    So what&rsquo;s happening?
  </p>

  <p>
    Well, you may remember in the last essay
    I <a href="/qcvc#hadamard-twice">asked you</a> to guess what would
    happen if you applied the Hadamard gate twice in a row to the
    $|0\rangle$ state. Intuitively, the Hadamard gate mixes the
    $|0\rangle$ and $|1\rangle$ states, so you might guess the end
    result would be to thoroughly mix them up.
  </p>

  <p>
    Only that&rsquo;s not what happens. Instead, here&rsquo;s the two steps:
  </p>

  <p>
    $$|0\rangle \rightarrow \frac{|0\rangle+|1\rangle}{\sqrt 2} \rightarrow
    \frac{\frac{|0\rangle+|1\rangle}{\sqrt 2} + \frac{|0\rangle-|1\rangle}{\sqrt 2}}{\sqrt 2}.$$
  </p>

  <p>
    If you look closely at the final expression on the right-hand side
    you see that the opposite signs on the two $|1\rangle$ states are
    canceling each other out, a phenomenon called <em>destructive
    interference</em>.  Meanwhile, the two $|0\rangle$ states add up,
    a phenomenon called <em>constructive interference</em>.  The net
    result is to concentrate all the amplitude in the $|0\rangle$
    state, and so the outcome is just $|0\rangle$.
  </p>

  <p>
    A similar (though more complicated) type of interference and sign
    cancellation is going on during the Grover iteration. Suppose we
    start out the iteration as follows:
  </p>

  <p>
      <center>
	<canvas id="start_search" width="340" height="200">
	</canvas>
      </center>
  </p>

  <p>
    We then reflect about the $|s\rangle$ state, which inverts all the
    non-$s$ amplitudes:
  </p>

  <p>
    <center>
      <canvas id="reflect_s" width="340" height="200">
      </canvas>
    </center>
  </p>

  <p>
    The next step is to reflect about the $|E\rangle$ state.  The
    effect of this step is to achieve a cancellation similar (but more
    complicated) than was going on in the second stage of the
    double-Hadamard. In particular, this reflection reduces the
    $|s\rangle$ amplitude a little, redistributing it over all the
    other computational basis states.  At the same time, it takes the
    superposition over all the other states and reduces it slightly,
    redistributing it to the $|s\rangle$ state. The net effect is to
    grow the $|s\rangle$ amplitude and shrink the others, albeit
    &ldquo;upside down&rdquo;:
  </p>

  <p>
    <center>
      <canvas id="reflect_e" width="340" height="240">
      </canvas>
    </center>
  </p>

  <script src="assets2/canvas.js">
  </script>
  <script src="assets2/search_amplitudes.js">
  </script>

  <p>
    The $-1$ global phase factor simply inverts everything, and the
    total effect is to grow the $|s\rangle$ amplitude and shrink the
    others. In net, we&rsquo;ve used the $|s\rangle$ amplitude to
    cancel out some of the other amplitudes (destructive
    interference), and the other amplitudes to reinforce some of the
    $|s\rangle$ amplitude (constructive interference).
  </p>
  
  <p>
    This explanation is, alas, somewhat vague. I wish I could write it
    in a clearer way, but I can&rsquo;t because I don&rsquo;t really understand it
    in a clearer way.  There&rsquo;s more I could say &ndash; other
    perspectives, other calculations we could do. Going through all
    that would help, but only a little.  At the core is still a clever
    way of using the $|s\rangle$-amplitude to cancel out
    non-$|s\rangle$ amplitudes, and to use non-$|s\rangle$ amplitudes
    to reinforce the $|s\rangle$-amplitude.
  </p>

  <p>
    <strong>Quantum parallelism:</strong> One thing the quantum search
    algorithm has in common with many other quantum algorithms is the
    use of large superposition states. For instance, the equal
    superposition state $|E\rangle = \sum_x |x\rangle/\sqrt{N}$ shows
    up in many quantum algorithms. It&rsquo;s a pretty common pattern in
    those algorithms to then modify that state so each term
    $|x\rangle$ picks up some information relevant to the solution of
    the overall problem, and then to trying to arrange cancellation of
    terms. This pattern is often known as <em>quantum
    parallelism</em>.
  </p>

  <p>
    Upon first acquaintance, this seems much like a conventional
    classical computer running a randomized (i.e., Monte Carlo)
    algorithm. In particular, it&rsquo;s a bit like trying a random
    solution, and then computing some information relevant to the
    overall problem you&rsquo;re trying to solve. But what is very different
    is that in a classical computer there&rsquo;s no way of getting
    cancellation between different possible solutions. The ability to
    get that kind of interference is crucial to quantum computing.
  </p>

  <p>
    <strong>Why we use clean computation:</strong> Earlier, I promised
    you an explanation of why we used clean computation. In fact, for
    the interference to work, it&rsquo;s essential that no other qubits are
    changed by the computation. Suppose we had a sum involving working
    qubits in lots of different states, something like (omitting
    factors) $\sum_x |x\rangle|w(x)\rangle$, i.e., a non-clean
    computation. We couldn&rsquo;t get any sort of cancellation (or
    reinforcement) between terms with different values of
    $w(x)$. Those other qubits would be storing information which
    prevented cancellation of terms. That&rsquo;s why clean computation is
    helpful.
  </p>

  <p>
    This helps explain why clean computation is useful, but may leave
    you wondering how you could ever have invented clean computation
    in the first place?
  </p>

  <p>
    In fact, historically the uncomputation trick for clean
    computation was discovered for reasons having nothing to do with
    quantum algorithms or with interference. It was discovered by
    people who were trying to figure out how to make conventional
    classical computers more energy efficient.  Those people had come
    up with an argument (which I won&rsquo;t describe here) that working
    bits actually contributed to energy inefficiency. And so they
    discovered uncomputation as a way of minimizing that energy
    cost. It was only later that it was realized that clean
    computation was extremely useful in quantum computing, especially
    for getting interference effects.
  </p>

  <p>
    This is a pattern which often occurs in creative work far beyond
    physics: ideas which arise in one context are often later reused
    for completely different reasons in another context. I believe
    that if that prior work on energy-efficient computing hadn&rsquo;t
    been done, it might have taken quite a bit more effort to come up
    with the quantum search algorithm.
  </p>

  <p>
    <strong>Concluding thoughts:</strong> You&rsquo;ve now worked through
    the details of a powerful, widely-usable quantum algorithm. Other
    algorithms are known, some of which &ndash; notably, the quantum
    algorithm for factoring integers &ndash; offer even larger
    speedups over known classical algorithms. Still, if and when
    quantum computers are eventually built the quantum search
    algorithm is likely to be an extremely useful application.  What&rsquo;s
    more, many of the ideas used in the search algorithm are used in
    other quantum algorithms.
  </p>

  <p class="curUser">
    Thanks for reading this far. In the coming weeks, you&rsquo;ll receive a 
    notification containing a link to your first review session. In that
    review session you&rsquo;ll be retested on the material you&rsquo;ve learned,
    helping you further commit it to memory. It should only take a few
    minutes. In subsequent weeks you&rsquo;ll receive more notifications
    linking you to re-review, gradually working toward genuine long-term
    memory of all the core material in the essay.
  </p>

  <div class="newUser">
    <p>
      Thanks for reading this far. If you&rsquo;d like to remember the
      core ideas of this essay durably, please set up an account
      below. We&rsquo;ll track your review schedule and send you
      occasional reminders containing links that will take you to the
      review experience.
    </p>
    <div class="Login"></div>
  </div>
  
  <div class="Footer">

    <h3>Acknowledgments</h3>

  <p>
    Michael Nielsen is supported by <a href="http://ycr.org">Y
      Combinator Research</a>.
  </p>

<h3>Citing this work</h3>
  <p class="CitationPreamble">In academic work, please cite this
    as:</p>
  
  <blockquote class="Citation">Andy Matuschak and Michael A. Nielsen,
    &ldquo;How does the quantum search algorithm work?&rdquo;,
    https://quantum.country/search, San Francisco (2019).</blockquote>
  
  <p class="AuthorOrderDisclaimer">Authors are listed in alphabetical order.</p>

  <h3>License</h3>
  <p class="License">This work is licensed under a
    <a href="http://creativecommons.org/licenses/by-nc/3.0/deed.en_GB">
      Creative Commons Attribution-NonCommercial 3.0 Unported
      License</a>. This means you&rsquo;re free to copy, share, and build
    on this essay, but not to sell it. If you&rsquo;re interested in
    commercial use,
    please <a href="mailto:mn@michaelnielsen.org;andy@andymatuschak.org">contact us</a>.
  </p>

  <h3>Last updated</h3>
  
  <p>April 16, 2019</p>

  <p>See our <a href="/terms" target="none">User Agreement and Privacy Policy</a>.</p>
</div>


    </div>
  </div>
`;
